import { useLingui } from "@lingui/react/macro";
import { classNames } from "@offstreetparking/design-system/utils";
import { BottomBar } from "@offstreetparking/portal-flow/components/layouts/BottomBar";
import { cva } from "class-variance-authority";
import { Footer } from "../Footer";
import { ParkerUserOverlay } from "../ParkerUserOverlay";
import { TopBar, type TopBarProps } from "../TopBar";
type CommonLayoutProps = {
	infoSlot?: React.ReactNode;
	returnToLink?: React.ReactNode;
	srOnlyTitle?: string;
	children: React.ReactNode;
	variant?: "default" | "account" | "auth";
	hideParkerSetup?: boolean;
} & TopBarProps;
const mainStyles = cva("flex justify-center ", {
	variants: {
		variant: {
			default: "h-full gap-16 lg:p-16 print:h-auto",
			account:
				"flex-1 flex-col items-center py-6 max-md:pb-0 sm:px-8 md:pb-20 lg:gap-6 lg:bg-neutral-faded lg:px-16 lg:py-16",
			auth: "flex-1 flex-col items-center py-6 pb-20 sm:px-8 lg:gap-6 lg:bg-neutral-faded lg:px-16 lg:py-16",
		},
	},
	defaultVariants: {
		variant: "default",
	},
});
const mainContentStyles = cva("outline-none", {
	variants: {
		variant: {
			default: "contents",
			account: "flex min-h-0 w-full max-w-2xl flex-1 flex-col rounded-lg bg-white",
			auth: "flex w-full max-w-lg flex-col rounded-lg bg-white",
		},
	},
	defaultVariants: {
		variant: "default",
	},
});
export const CommonLayout = ({
	children,
	infoSlot,
	returnToLink,
	srOnlyTitle,
	variant = "default",
	hideParkerSetup,
	...topBarProps
}: CommonLayoutProps) => {
	const { t } = useLingui();
	return (
		<div
			className={classNames(
				"flex min-h-screen flex-col",
				variant === "account" ? "max-md:h-dvh max-md:min-h-0 max-md:overflow-hidden" : "",
			)}
		>
			<a
				href="#main-content"
				className="sr-only focus:not-sr-only focus:absolute focus:z-50 focus:bg-neutral-faded focus:p-4 focus:text-black focus:underline"
			>
				{t`Skip to main content`}
			</a>
			<div
				className={classNames(
					"flex grow flex-col",
					variant === "account"
						? "max-md:min-h-0 max-md:overflow-y-auto max-md:overflow-x-hidden max-md:overscroll-contain"
						: "",
				)}
			>
				<TopBar {...topBarProps} hideTopBar={variant === "account" && !!returnToLink} />

				<main
					className={mainStyles({
						variant,
					})}
				>
					{srOnlyTitle && <h1 className="sr-only xl:hidden">{srOnlyTitle}</h1>}
					{infoSlot && <div className="hidden w-[475px] justify-center xl:flex print:hidden">{infoSlot}</div>}
					{!!returnToLink && <div className="w-full max-w-2xl">{returnToLink}</div>}
					<div
						id="main-content"
						tabIndex={-1}
						className={classNames(
							mainContentStyles({
								variant,
							}),
						)}
					>
						{children}
					</div>
				</main>
			</div>
			<Footer />
			<BottomBar isInline={variant === "account"} />
			<ParkerUserOverlay hideParkerSetup={hideParkerSetup} />
		</div>
	);
};
